<template>
  <div>
    <v-layout align-content-space-between align-center row class="my-2">
      <v-flex xs6>
        <v-layout align-content-space-between align-center row>
          <v-flex shrink class="mr-2">
            <v-img
              v-if="employee.profile_picture_url"
              height="200"
              width="200"
              contain
              :src="employee.profile_picture_url"
            >
            </v-img>
          </v-flex>
          <v-flex shrink>
            <InternalNotesWidget
              v-if="user.managerOrHigher"
              entityName="User"
              :entityId="employee.id"
            />

            <h2>
              <EmployeeName :employee="employee" showColor />
            </h2>
            <h2 class="subtle">
              Position{{ employee.position_names.length > 1 ? "s" : "" }}:
              <div class="inline-list">
                <p>
                  <span v-for="position in employee.position_names">{{
                    position
                  }}</span>
                </p>
              </div>
            </h2>
            <h2 class="subtle">Standing: {{ employee.standing }}</h2>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex xs4>
        <div v-if="currently.shiftId">
          <h2 class="heading">Currently: {{ currently.status }}</h2>
          <h2 class="subheading">
            <router-link
              :to="{
                name: 'shiftDetail',
                params: { shiftId: currently.shiftId }
              }"
            >
              {{ currently.position }} at {{ currently.locationName }}
            </router-link>
          </h2>
          <h2 class="subheading">
            Shift started at {{ currently.startTime | moment("h:mma MMMM DD") }}
          </h2>
        </div>
        <div v-else>
          <h2 class="heading">Not currently working</h2>
        </div>
      </v-flex>
      <v-flex xs2 class="text-xs-right">
        <v-layout column>
          <v-flex>
            <NewTaskDialog :managed="true" :forUserID="employee.id">
              <v-btn color="success">
                <v-icon left> check_circle </v-icon>
                Assign Task
              </v-btn>
            </NewTaskDialog>
          </v-flex>
          <v-flex>
            <v-btn
              color="primary"
              :to="{ name: 'managedTodoLists', query: { userID: employee.id } }"
            >
              View Tasks
            </v-btn>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-layout row>
      <!-- small bar -->
      <v-toolbar color="primary" flat dense class="hidden-md-and-up">
        <v-menu>
          <template v-slot:activator="{ on }">
            <v-toolbar-title v-on="on">
              <span class="white--text">
                {{ componentNameReadable }}
                <v-icon color="white">arrow_drop_down</v-icon>
              </span>
            </v-toolbar-title>
          </template>
          <v-list>
            <v-list-tile
              v-for="item in tabOptions"
              :key="`${item.routeName}-1`"
              :to="{ name: item.routeName }"
            >
              <v-list-tile-title v-text="item.linkLabel"></v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-toolbar>

      <!-- large bar -->
      <v-toolbar
        color="primary"
        flat
        dense
        class="hidden-sm-and-down"
        style="overflow-x: auto;"
      >
        <v-toolbar-items
          v-for="item in tabOptions"
          :key="`${item.routeName}-1`"
        >
          <v-btn small flat color="white" :to="{ name: item.routeName }" exact>
            {{ item.linkLabel }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
    </v-layout>

    <v-layout row>
      <component :is="componentName" v-bind="{ employee, user }"></component>
    </v-layout>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import TopSheet from "@/components/hr/top-sheet.vue";
import Info from "@/components/hr/info.vue";
import Experience from "@/components/hr/experience.vue";
import Certifications from "@/components/hr/certifications.vue";
import Manage from "@/components/hr/manage.vue";
import Admin from "@/components/hr/admin.vue";
import Team from "@/components/hr/team.vue";
import EmployeePersonnelHistory from "@/components/hr/employee-personnel-history.vue";
import Schedule from "@/components/hr/schedule.vue";
import WorkHistory from "@/components/hr/work-history.vue";
import HeaderBar from "@/components/base/header-bar.vue";
import NewTaskDialog from "../todos/new-todo-item-dialog.vue";

export default {
  components: {
    TopSheet,
    Info,
    EmployeePersonnelHistory,
    Experience,
    Certifications,
    Manage,
    EmployeePersonnelHistory,
    Admin,
    Team,
    Schedule,
    WorkHistory,
    HeaderBar,
    NewTaskDialog
  },
  props: ["user"],
  created() {
    this.$store.dispatch(
      "employees/getManagersForDetailedEmployee",
      this.$route.params.employeeId
    );
    this.$store.dispatch(
      "employees/getPrimaryPositionsForDetailedEmployee",
      this.$route.params.employeeId
    );
    this.$store.dispatch("employees/getCurrent", this.$route.params.employeeId);
  },
  computed: {
    ...mapState("employees", ["detailedEmployee", "detailedEmployeeCurrently"]),
    employee() {
      return this.detailedEmployee;
    },
    currently() {
      return this.detailedEmployeeCurrently;
    },
    componentName() {
      const currentComponent = this.tabOptions.find(o => {
        return o.routeName === this.$router.currentRoute.name;
      });

      if (!currentComponent) {
        this.$router.push({ name: "employee/top-sheet" });
        return "TopSheet";
      }

      return currentComponent.componentName;
    },
    componentNameReadable() {
      const currentComponent = this.tabOptions.find(o => {
        return o.routeName === this.$router.currentRoute.name;
      });

      if (!currentComponent) {
        this.$router.push({ name: "employee/top-sheet" });
        return "Top Sheet";
      }

      return currentComponent.linkLabel;
    }
  },
  data() {
    return {
      tabOptions: [
        {
          routeName: "employee/top-sheet",
          linkLabel: "Top Sheet",
          componentName: "TopSheet"
        },
        {
          routeName: "employee/info",
          linkLabel: "Info",
          componentName: "Info"
        },
        {
          routeName: "employee/experience",
          linkLabel: "Experience",
          componentName: "Experience"
        },
        {
          routeName: "employee/certifications",
          linkLabel: "Certifications",
          componentName: "Certifications"
        },
        {
          routeName: "employee/admin",
          linkLabel: "Admin",
          componentName: "Admin"
        },
        {
          routeName: "employee/manage",
          linkLabel: "Manage",
          componentName: "Manage"
        },
        {
          routeName: "employee/personnel-history",
          linkLabel: "Personnel History",
          componentName: "EmployeePersonnelHistory"
        },
        {
          routeName: "employee/team",
          linkLabel: "Team",
          componentName: "Team"
        },
        {
          routeName: "employee/schedule",
          linkLabel: "Schedule",
          componentName: "Schedule"
        },
        {
          routeName: "employee/work-history",
          linkLabel: "Work History",
          componentName: "WorkHistory"
        }
      ]
    };
  }
};
</script>
